import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TRADING_NAME } from "@/constants/company";

interface ContractSigningModalProps {
  signingUrl: string | null;
  open: boolean;
  onClose: () => void;
  onCompleted?: () => void | Promise<void>;
}

declare global {
  interface Window {
    SignWellEmbed?: new (options: {
      url: string;
      containerId?: string;
      allowDecline?: boolean;
      events?: {
        completed?: (e: unknown) => void;
        closed?: (e: unknown) => void;
        declined?: (e: unknown) => void;
      };
    }) => { open: () => void };
  }
}

function loadSignWellScript(): Promise<void> {
  if (window.SignWellEmbed) {
    return Promise.resolve();
  }

  const existing = document.querySelector<HTMLScriptElement>("script[data-signwell-embed]");
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("SignWell embed failed to load")), { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://static.signwell.com/assets/embedded.js";
    script.async = true;
    script.dataset.signwellEmbed = "true";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("SignWell embed failed to load"));
    document.body.appendChild(script);
  });
}

export default function ContractSigningModal({ signingUrl, open, onClose, onCompleted }: ContractSigningModalProps) {
  const openedForUrl = useRef<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !signingUrl) {
      openedForUrl.current = null;
      return;
    }
    if (openedForUrl.current === signingUrl) {
      return;
    }

    let cancelled = false;
    openedForUrl.current = signingUrl;

    loadSignWellScript()
      .then(() => {
        if (cancelled || !window.SignWellEmbed) return;
        const embed = new window.SignWellEmbed({
          url: signingUrl,
          containerId: "signwell-container",
          allowDecline: true,
          events: {
            completed: () => {
              void Promise.resolve(onCompleted?.()).finally(() => onClose());
            },
            closed: () => onClose(),
            declined: () => onClose(),
          },
        });
        embed.open();
      })
      .catch(() => {
        openedForUrl.current = null;
      });

    return () => {
      cancelled = true;
    };
  }, [open, signingUrl, onClose, onCompleted]);

  if (!open || !signingUrl) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="relative flex h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0d1b2e] shadow-2xl">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          <div>
            <p className="font-display text-sm font-semibold text-white">Sign your {TRADING_NAME} agreement</p>
            <p className="text-xs text-white/50">Secure electronic signature powered by SignWell</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="text-white/70 hover:text-white">
            <X className="h-5 w-5" />
          </Button>
        </div>
        <div id="signwell-container" className="h-full w-full flex-1 bg-white" />
      </div>
    </div>
  );
}
